import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import AuthModal from "../components/AuthModal";
import Toast from "../components/Toast";
import { slugify } from "../utils/slugify";

const API = process.env.REACT_APP_API_BASE_URL;

/* ─── Star renderer ─────────────────────────────────────────────────────── */
function Stars({ rating, interactive = false, onRate, size = "sm" }) {
  const [hovered, setHovered] = useState(0);
  const sz = size === "lg" ? 26 : size === "md" ? 18 : 13;
  return (
    <span style={{ display: "inline-flex", gap: size === "lg" ? 5 : 3 }}>
      {[1, 2, 3, 4, 5].map((n) => {
        const filled = interactive ? n <= (hovered || rating) : n <= Math.round(rating);
        return (
          <svg
            key={n}
            width={sz} height={sz}
            viewBox="0 0 24 24"
            fill={filled ? "var(--accent)" : "none"}
            stroke="var(--accent)"
            strokeWidth="1.8"
            style={{ cursor: interactive ? "pointer" : "default", transition: "fill 0.15s" }}
            onMouseEnter={() => interactive && setHovered(n)}
            onMouseLeave={() => interactive && setHovered(0)}
            onClick={() => interactive && onRate && onRate(n)}
          >
            <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
          </svg>
        );
      })}
    </span>
  );
}

/* ─── Review Form ───────────────────────────────────────────────────────── */
function ReviewForm({ productId, token, onSubmitted }) {
  const [rating, setRating]         = useState(0);
  const [comment, setComment]       = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]           = useState("");

  const submit = async () => {
    if (rating === 0)    { setError("Please select a rating."); return; }
    if (!comment.trim()) { setError("Please write a comment."); return; }
    setSubmitting(true);
    setError("");
    try {
      await axios.post(
        `${API}/products/${productId}/reviews`,
        { rating, comment },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRating(0);
      setComment("");
      onSubmitted();
    } catch (e) {
      setError(e.response?.data?.message || "Failed to submit review.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="pd2-review-form">
      <div className="pd2-review-form-rating">
        <span className="pd2-form-label">Your Rating</span>
        <Stars rating={rating} interactive onRate={setRating} size="lg" />
      </div>
      <div className="pd2-review-form-field">
        <label className="pd2-form-label">Your Review</label>
        <textarea
          className="pd2-textarea"
          rows={4}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Share your experience with this product…"
        />
      </div>
      {error && <div className="pd2-error">{error}</div>}
      <button className="pd2-btn-primary" style={{ alignSelf: "flex-start" }} onClick={submit} disabled={submitting}>
        {submitting ? (
          <><div className="pd2-btn-spinner" />Submitting…</>
        ) : (
          <>Submit Review
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </>
        )}
      </button>
    </div>
  );
}

/* ─── Review Card ───────────────────────────────────────────────────────── */
function ReviewCard({ review, index }) {
  const name = review.userName || review.user?.name || "Anonymous";
  return (
    <div className="pd2-review-card" style={{ animationDelay: `${index * 0.07}s` }}>
      <div className="pd2-review-top">
        <div className="pd2-reviewer">
          <div className="pd2-avatar">{name[0].toUpperCase()}</div>
          <div className="pd2-reviewer-info">
            <span className="pd2-reviewer-name">{name}</span>
            <span className="pd2-reviewer-badge">Verified Purchase</span>
          </div>
        </div>
        <Stars rating={review.rating} />
      </div>
      <p className="pd2-review-text">{review.comment}</p>
    </div>
  );
}

/* ─── Skeleton ──────────────────────────────────────────────────────────── */
function Skeleton() {
  return (
    <div className="pd2-skel-page">
      <div className="pd2-skel-inner">
        {/* Image side: vertical thumbs + main image */}
        <div className="pd2-skel-img-col">
          <div className="pd2-skel-thumbs">
            {[0, 1, 2].map(i => <div key={i} className="pd2-s pd2-skel-thumb" />)}
          </div>
          <div className="pd2-s pd2-skel-main" />
        </div>
        {/* Info side */}
        <div className="pd2-skel-info">
          <div className="pd2-s" style={{ height: 9, width: "22%", marginBottom: 14 }} />
          <div className="pd2-s" style={{ height: 54, width: "92%", marginBottom: 8 }} />
          <div className="pd2-s" style={{ height: 54, width: "65%", marginBottom: 22 }} />
          <div className="pd2-s" style={{ height: 13, width: "40%", marginBottom: 22 }} />
          <div className="pd2-s" style={{ height: 50, width: "42%", marginBottom: 26 }} />
          <div className="pd2-s" style={{ height: 1, marginBottom: 22 }} />
          <div className="pd2-s" style={{ height: 13, marginBottom: 10 }} />
          <div className="pd2-s" style={{ height: 13, marginBottom: 10 }} />
          <div className="pd2-s" style={{ height: 13, width: "68%", marginBottom: 32 }} />
          <div className="pd2-s" style={{ height: 46, width: 185, borderRadius: 2 }} />
        </div>
      </div>
    </div>
  );
}

/* ─── Main Component ────────────────────────────────────────────────────── */
export default function ProductDetails({ cart, setCart }) {
  const { id: idParam } = useParams();
  const id = idParam.split("-")[0]; // works for both "6" and "6-grilled-chicken"
  const navigate = useNavigate();
  const [product, setProduct]     = useState(null);
  const [reviews, setReviews]     = useState([]);
  const [activeImg, setActiveImg] = useState(null);
  const [loading, setLoading]     = useState(true);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [showAuth, setShowAuth]   = useState(false);
  const [reviewState, setReviewState] = useState("idle");
  const [showToast, setShowToast] = useState(false);
  const [thumbStart, setThumbStart] = useState(0);
  const reviewsRef = useRef(null);
  const addToCartActionsRef = useRef(null);
  const [cartBtnVisible, setCartBtnVisible] = useState(false);
  const [cartScrolledPast, setCartScrolledPast] = useState(false);
  const THUMB_LIMIT = 3;

  const addToCart = () => {
    if (!product) return;
    const existing = cart.find((p) => p.id === product.id);
    if (existing) {
      setCart(cart.map((p) => p.id === product.id ? { ...p, quantity: p.quantity + 1 } : p));
    } else {
      setCart([...cart, { ...product, quantity: 1 }]);
    }
    setShowToast(true);
  };

  const token  = localStorage.getItem("token");
  const userId = localStorage.getItem("userId");

  const loadProduct = useCallback(() =>
    axios.get(`${API}/products/${id}/details`).then(r => {
      const data = r.data;
      setProduct(data);
      setActiveImg(data.imageUrl);
      const rv = Array.isArray(data.reviews) ? data.reviews : [];
      setReviews(rv);
      if (userId && rv.length > 0) {
        const alreadyReviewed = rv.some(r => String(r.user?.id) === String(userId));
        if (alreadyReviewed) setReviewState("reviewed");
      }
    }), [id, userId]);

  useEffect(() => {
    setLoading(true);
    setReviewState("idle");
    setThumbStart(0);
    loadProduct().catch(console.error).finally(() => setLoading(false));
  }, [id, loadProduct]);

  /* Floating Add-to-Cart: track when the in-page button leaves/enters viewport */
  useEffect(() => {
    const el = addToCartActionsRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        setCartBtnVisible(entry.isIntersecting);
        if (!entry.isIntersecting) {
          setCartScrolledPast(entry.boundingClientRect.top < 0);
        }
      },
      { threshold: 0 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [product]);

  /* Scroll-reveal for review cards */
  useEffect(() => {
    if (!reviews.length) return;
    const io = new IntersectionObserver(
      (entries) => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add("vis"); }),
      { threshold: 0.08 }
    );
    reviewsRef.current?.querySelectorAll(".pd2-review-card").forEach(c => io.observe(c));
    return () => io.disconnect();
  }, [reviews]);

  const handleWriteReviewClick = async () => {
    if (!token) { setShowAuth(true); return; }
    setReviewState("checking");
    try {
      const r = await axios.get(`${API}/orders/has-purchased/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const purchased = r.data === true || r.data?.purchased === true;
      setReviewState(purchased ? "eligible" : "not-purchased");
    } catch {
      setReviewState("not-purchased");
    }
  };

  const handleReviewSubmitted = () => {
    setReviewState("reviewed");
    loadProduct().catch(console.error);
  };

  useEffect(() => {
    if (product) document.title = `${product.name} | Tablecure`;
    return () => { document.title = "Tablecure"; };
  }, [product]);

  /* Silently replace /product/6 → /product/6-grilled-chicken in the address bar */
  useEffect(() => {
    if (!product) return;
    const expected = `${product.id}-${slugify(product.name)}`;
    if (idParam !== expected) {
      window.history.replaceState(null, "", `/product/${expected}`);
    }
  }, [product, idParam]);

  if (loading) return <Skeleton />;
  if (!product) return (
    <div className="tc-loading">
      <div className="tc-spinner" />
      <p style={{ letterSpacing: "0.08em", fontSize: 12 }}>PRODUCT NOT FOUND</p>
    </div>
  );

  const allImages   = [product.imageUrl, ...(product.images || [])].filter(Boolean);
  const allMedia    = [...allImages, ...(product.videoUrl ? [product.videoUrl] : [])];
  const isActiveVideo = !!product.videoUrl && activeImg === product.videoUrl;
  const hasSpecs    = product.specifications && Object.keys(product.specifications).length > 0;
  const hasFeatures = product.features && product.features.length > 0;
  const specEntries = hasSpecs ? Object.entries(product.specifications) : [];

  return (
    <>
      {/* ══ HERO ══════════════════════════════════════════════════════════════ */}
      <section className="pd2-hero">
        {/* Background atmosphere */}
        <div aria-hidden="true" className="pd2-bg">
          <div className="pd2-orb pd2-orb1" />
          <div className="pd2-orb pd2-orb2" />
          <div className="pd2-orb pd2-orb3" />
          <div className="pd2-grid" />
        </div>

        <div className="pd2-hero-inner">

          {/* ── LEFT: Image Gallery — vertical thumb strip + main image ── */}
          <div className="pd2-img-col">
            {/* Thumbnail carousel: 3 visible at a time, nav arrows to scroll */}
            {allMedia.length > 1 && (
              <div className="pd2-thumbs-carousel">
                {allMedia.length > THUMB_LIMIT && (
                  <button
                    className="pd2-thumbs-nav pd2-thumbs-nav-prev"
                    onClick={() => setThumbStart(s => Math.max(0, s - 1))}
                    disabled={thumbStart === 0}
                    aria-label="Previous images"
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="6,9 12,15 18,9" />
                    </svg>
                  </button>
                )}
                <div className="pd2-thumbs-viewport">
                  <div className="pd2-thumbs-vert" style={{ "--thumb-offset": thumbStart }}>
                    {allMedia.map((url) => {
                      const isVid = url === product.videoUrl;
                      return (
                        <button
                          key={url}
                          className={"pd2-thumb-vert" + (activeImg === url ? " active" : "")}
                          onClick={() => { if (!isVid) setImgLoaded(false); setActiveImg(url); }}
                        >
                          {isVid ? (
                            <div style={{ position: "relative", width: "100%", height: "100%" }}>
                              <video
                                src={url}
                                muted
                                preload="metadata"
                                style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                                onLoadedMetadata={(e) => { e.target.currentTime = 0.001; }}
                              />
                              <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.35)" }}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="white" stroke="none">
                                  <polygon points="5,3 19,12 5,21" />
                                </svg>
                              </div>
                            </div>
                          ) : (
                            <img src={url} alt="" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
                {allMedia.length > THUMB_LIMIT && (
                  <button
                    className="pd2-thumbs-nav pd2-thumbs-nav-next"
                    onClick={() => setThumbStart(s => Math.min(allMedia.length - THUMB_LIMIT, s + 1))}
                    disabled={thumbStart + THUMB_LIMIT >= allMedia.length}
                    aria-label="Next images"
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="6,9 12,15 18,9" />
                    </svg>
                  </button>
                )}
              </div>
            )}

            {/* Main image */}
            <div className="pd2-main-wrap">
              {!imgLoaded && !isActiveVideo && <div className="pd2-img-skel" />}
              {isActiveVideo ? (
                <video
                  key={activeImg}
                  src={activeImg}
                  controls
                  style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                />
              ) : (
                <img
                  src={activeImg}
                  alt={product.name}
                  className="pd2-main-img"
                  style={{ opacity: imgLoaded ? 1 : 0 }}
                  onLoad={() => setImgLoaded(true)}
                />
              )}
              <div className="pd2-img-overlay" />
              <div className="pd2-img-corner pd2-img-corner-tl" />
              <div className="pd2-img-corner pd2-img-corner-br" />
            </div>
          </div>

          {/* ── RIGHT: Product Info ── */}
          <div className="pd2-info">
            <div className="pd2-eyebrow">
              <span className="pd2-eyebrow-dot" />
              Menu Item
            </div>

            <h1 className="pd2-title">{product.name}</h1>

            <div className="pd2-rating-row">
              <Stars rating={product.avgRating} size="md" />
              <span className="pd2-rating-val">{product.avgRating?.toFixed(1)}</span>
              <span className="pd2-rating-count">
                ({product.totalReviews} {product.totalReviews === 1 ? "review" : "reviews"})
              </span>
            </div>

            <div className="pd2-price-row">
              {product.salePrice != null ? (
                <>
                  {product.mrp != null && product.mrp > product.price && (
                    <span className="pd2-price-mrp">₹{Number(product.mrp).toLocaleString("en-IN")}</span>
                  )}
                  <span className="pd2-price-was">₹{Number(product.price).toLocaleString("en-IN")}</span>
                  <span className="pd2-price pd2-price-sale">₹{Number(product.salePrice).toLocaleString("en-IN")}</span>
                  {product.salePercent != null && (
                    <span className="pd2-sale-badge">{product.salePercent}% OFF</span>
                  )}
                </>
              ) : product.mrp != null && product.mrp > product.price ? (
                <>
                  <span className="pd2-price-mrp">₹{Number(product.mrp).toLocaleString("en-IN")}</span>
                  <span className="pd2-price-currency">₹</span>
                  <span className="pd2-price">{Number(product.price).toLocaleString("en-IN")}</span>
                </>
              ) : (
                <>
                  <span className="pd2-price-currency">₹</span>
                  <span className="pd2-price">{Number(product.price).toLocaleString("en-IN")}</span>
                </>
              )}
            </div>

            <div className="pd2-divider" />

            <p className="pd2-desc">{product.description}</p>

            {hasFeatures && (
              <div className="pd2-chips">
                {product.features.map((f, i) => (
                  <span key={i} className="pd2-chip">{f}</span>
                ))}
              </div>
            )}

            <div className="pd2-actions" ref={addToCartActionsRef}>
              <button className="pd2-btn-primary" onClick={addToCart}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                  <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z" />
                  <line x1="3" y1="6" x2="21" y2="6" />
                  <path d="M16 10a4 4 0 01-8 0" />
                </svg>
                Add to Cart
              </button>
              <button className="pd2-btn-ghost">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z" />
                </svg>
                Wishlist
              </button>
            </div>

            <div className="pd2-trust">
              <span>Free Shipping</span>
              <span className="pd2-trust-sep" />
              <span>7-Day Returns</span>
              <span className="pd2-trust-sep" />
              <span>2-Year Warranty</span>
            </div>
          </div>

        </div>
      </section>

      {/* ══ SPEC STRIP ════════════════════════════════════════════════════════ */}
      {hasSpecs && specEntries.length > 0 && (
        <div className="pd2-spec-strip">
          <div className="pd2-spec-inner">
            {specEntries.slice(0, 5).map(([k, v]) => (
              <div key={k} className="pd2-spec-stat">
                <div className="pd2-spec-stat-val">{v}</div>
                <div className="pd2-spec-stat-lbl">{k}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ══ FULL SPECIFICATIONS ═══════════════════════════════════════════════ */}
      {hasSpecs && (
        <section className="pd2-section">
          <div className="pd2-section-inner">
            <div className="pd2-section-head">
              <div className="pd2-eyebrow"><span className="pd2-eyebrow-dot" />Technical</div>
              <h2 className="pd2-section-title">Specifications</h2>
              <div className="pd2-section-divider" />
            </div>
            <div className="pd2-specs-table">
              {specEntries.map(([k, v]) => (
                <div key={k} className="pd2-spec-row">
                  <div className="pd2-spec-key">{k}</div>
                  <div className="pd2-spec-value">{v}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ══ REVIEWS ═══════════════════════════════════════════════════════════ */}
      <section className="pd2-section pd2-section-alt">
        <div className="pd2-section-inner">
          <div className="pd2-section-head">
            <div className="pd2-eyebrow"><span className="pd2-eyebrow-dot" />Customer Feedback</div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 18, flexWrap: "wrap" }}>
              <h2 className="pd2-section-title">Reviews</h2>
              {reviews.length > 0 && (
                <span className="pd2-count-badge">{reviews.length} {reviews.length === 1 ? "review" : "reviews"}</span>
              )}
            </div>
            <div className="pd2-section-divider" />
          </div>

          {/* Rating summary */}
          {reviews.length > 0 && (
            <div className="pd2-rating-summary">
              <div className="pd2-rating-big">{product.avgRating?.toFixed(1)}</div>
              <div className="pd2-rating-summary-right">
                <Stars rating={product.avgRating} size="lg" />
                <p className="pd2-rating-base">Based on {reviews.length} {reviews.length === 1 ? "review" : "reviews"}</p>
              </div>
            </div>
          )}

          {/* Review action area */}
          <div className="pd2-review-action">
            {!token && (
              <div className="pd2-review-gate">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <span>
                  <button className="pd2-inline-link" onClick={() => setShowAuth(true)}>Sign in</button>
                  {" "}and purchase this item to leave a review.
                </span>
              </div>
            )}
            {token && reviewState === "idle" && (
              <div className="pd2-review-prompt">
                <p>Bought this product? Help others by sharing your experience.</p>
                <button className="pd2-btn-primary" style={{ fontSize: 11, padding: "10px 22px" }} onClick={handleWriteReviewClick}>
                  Write a Review
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                    <path d="M12 20h9M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z" />
                  </svg>
                </button>
              </div>
            )}
            {token && reviewState === "checking" && (
              <div className="pd2-review-status">
                <div className="tc-spinner" style={{ width: 13, height: 13 }} />
                <span>Checking eligibility…</span>
              </div>
            )}
            {token && reviewState === "not-purchased" && (
              <div className="pd2-review-gate">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <span style={{ color: "var(--text-muted)" }}>Only verified purchasers can leave a review.</span>
              </div>
            )}
            {token && reviewState === "eligible" && (
              <ReviewForm productId={id} token={token} onSubmitted={handleReviewSubmitted} />
            )}
            {token && reviewState === "reviewed" && (
              <div className="pd2-review-success">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--success-text)" strokeWidth="2.5">
                  <polyline points="20,6 9,17 4,12" />
                </svg>
                <span>You've already reviewed this product — thank you!</span>
              </div>
            )}
          </div>

          {/* Review list */}
          <div className="pd2-reviews-list" ref={reviewsRef}>
            {reviews.length > 0
              ? reviews.map((rv, i) => <ReviewCard key={rv.id ?? i} review={rv} index={i} />)
              : (
                <div className="pd2-no-reviews">
                  <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="var(--border-strong)" strokeWidth="1">
                    <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
                  </svg>
                  <p>No reviews yet.</p>
                  <span>Be the first to share your experience.</span>
                </div>
              )
            }
          </div>
        </div>
      </section>

      {/* Mobile floating Add to Cart — hidden on desktop via CSS */}
      {!cartBtnVisible && product && (
        <div
          key={cartScrolledPast ? "top" : "bottom"}
          className={`pd2-float-cart pd2-float-cart--${cartScrolledPast ? "top" : "bottom"}`}
        >
          <button className="pd2-btn-primary" onClick={addToCart}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
              <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z" />
              <line x1="3" y1="6" x2="21" y2="6" />
              <path d="M16 10a4 4 0 01-8 0" />
            </svg>
            Add to Cart
          </button>
        </div>
      )}

      <Toast
        show={showToast}
        setShow={setShowToast}
        title="Added to Cart"
        message={product?.name}
        variant="success"
        actions={[
          { label: "View Cart", variant: "primary", onClick: () => { setShowToast(false); navigate("/cart"); } },
        ]}
      />
      {showAuth && <AuthModal initialMode="login" onClose={() => setShowAuth(false)} />}
    </>
  );
}
